import {
  Input,
  useDisclosure,
  useToast,
  Text,
  Center,
  Icon,
  IconButton,
  HStack,
  Textarea,
  Box,
  Checkbox,
  VStack,
  Radio,
  RadioGroup,
  Stack,
} from "@chakra-ui/react";
import {
  fetchRules,
  fetchRulesById,
} from "../../services/state/rules/rules-async-calls";
import { AppDispatch, RootState } from "@/services/state/store";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { RuleInput } from "@/services/state/rules/rules-interface";
import {
  setSelectedRuleBody,
  setSelectedRuleInputData,
} from "../../services/state/rules/rules";
import { DropDown, Option } from "../../components/Select/DropDown";
import {
  fetchCustomDataTypesByApplication,
  fetchDataTypes,
} from "../../services/state/custom-data-types/customDataTypes-async-calls";
import { FaMinus } from "react-icons/fa6";
import { CiCircleChevUp, CiCircleChevDown } from "react-icons/ci";
import { getAxiosClient } from "../../services/HttpsService";
import { isStringLiteral } from "typescript";
import { useMonaco } from "@monaco-editor/react";
import { fetchRecordTypesByApplication } from "../../services/state/record-types/recordTypes-async-calls";

export const useRule = () => {
  const dispatch = useDispatch<AppDispatch>();
  const toast = useToast();
  const { ruleId } = useParams();
  const monaco = useMonaco();

  const { isOpen: isEditing, onToggle: onEditToggle } = useDisclosure();

  const [status, setStatus] = useState<string | null>(null);
  const [responseData, setResponseData] = useState<any>(null);

  const selectedRule = useSelector(
    (state: RootState) => state.rules.selectedRule
  );
  const recordTypes = useSelector(
    (state: RootState) => state.recordTypes.recordTypes
  );
  const allSelectedRuleInputs = useSelector(
    (state: RootState) => state.rules.selectedRuleInputsData
  );
  const selectedRuleInputs = useSelector(
    (state: RootState) => state.rules.selectedRuleInputsData
  ).filter((input) => input.isActive === "Y");
  const rules = useSelector((state: RootState) => state.rules.rules);

  const ruleMatchers = rules.map((rule) => ({
    name: `rule!${rule.ruleNm.replace(/ /g, "_")}`,
    value: rule.uuid,
  }));

  const recordTypeMatchers = recordTypes.map((rt) => ({
    name: `rv!${rt.recordName}`,
    value: rt.uuid,
  }));
  const selectedRuleBody = expressionHandlerReverse(
    useSelector((state: RootState) => state.rules.selectedRuleBody)
  );
  const dataTypes = useSelector(
    (state: RootState) => state.customDataTypes.dataTypes
  );
  const cdt = useSelector(
    (state: RootState) => state.customDataTypes.customDataTypes
  );

  const dataTypeOptions = dataTypes.map((type) => ({
    id: type.dataTypCd,
    name: type.dataTypNm,
    component: <Text>{type.dataTypNm}</Text>,
  }));

  const recordTypeOptions = recordTypes.map((rt) => ({
    id: rt.uuid,
    name: rt.recordName,
    component: <Text>{rt.recordName}</Text>,
  }));

  const cdtOptions = cdt.map((it) => ({
    id: it.uuid,
    name: it.dataTypeName,
    component: <Text>{it.dataTypeName}</Text>,
  }));

  useEffect(() => {
    dispatch(fetchRulesById(ruleId as string));
    dispatch(fetchDataTypes());
  }, [ruleId, dispatch]);

  useEffect(() => {
    if (selectedRule) {
      dispatch(fetchRules(selectedRule.app.uuid));
      dispatch(fetchCustomDataTypesByApplication(selectedRule.app.uuid));
      dispatch(fetchRecordTypesByApplication(selectedRule.app.uuid));
    }
  }, [selectedRule]);

  const defineGroovyLanguage = (monacoInstance: any) => {
    if (!monacoInstance) return;

    // Register Groovy language
    monacoInstance.languages.register({ id: "groovy" });

    // Define the syntax highlighting rules using Monarch tokens
    monacoInstance.languages.setMonarchTokensProvider("groovy", {
      tokenizer: {
        root: [
          [
            /\b(def|class|if|else|while|for|return|import|package|public|private|protected|static|void|true|false|null)\b/,
            "keyword",
          ],
          [/pv![a-zA-Z_$][\w$]*(?:\s*\([^\)]*\))?/, "constant"],
          [/rv![a-zA-Z_$][\w$]*(?:\s*\([^\)]*\))?/, "constant"],
          [/(rule![a-zA-Z_][a-zA-Z0-9_]*|exp!)(?=\s*\([^)]*\))/, "comment"],
          [/[{}()\[\]]/, "@brackets"],
          [/"/, "string", "@string"],
          [/\b\d+(\.\d+)?\b/, "number"],
          [/\/\*[\s\S]*?\*\//, "comment"],
          [/\/\/.*$/, "comment"],
          [/\bdbService\b/, "number"],
          [
            /\b(writeRecord|queryRecords|updateRecord|deleteRecord)\b/,
            "number",
          ],
        ],
        string: [
          [/[^\\"]+/, "string"],
          [/\\./, "string.escape"],
          [/"/, "string", "@pop"],
        ],
      },
    });

    // Define custom theme for Groovy syntax highlighting
    monacoInstance.editor.defineTheme("custom-theme", {
      base: "vs",
      inherit: true,
      rules: [
        { token: "keyword", foreground: "0000FF" },
        { token: "constant", foreground: "008000", fontStyle: "italic" },
        { token: "comment", foreground: "FFA500", fontStyle: "italic" },
        { token: "string", foreground: "008080" },
        { token: "number", foreground: "008000" },
        { token: "dbService", foreground: "007acc", fontStyle: "bold" },
        {
          token: "dbService-method",
          foreground: "008000",
          fontStyle: "italic",
        },
      ],
      colors: {
        "editor.foreground": "#000000",
        "editor.background": "#FFFFFF",
      },
    });

    const uniqueRuleMatchers = Array.from(new Set(ruleMatchers));
    const uniqueRecordTypeMatchers = Array.from(new Set(recordTypeMatchers));

    // Register completion provider for custom rule suggestion
    const provider = monacoInstance.languages.registerCompletionItemProvider(
      "groovy",
      {
        triggerCharacters: ["!", "(", " ", "."],
        provideCompletionItems: (model: any, position: any) => {
          const textBeforeCursor = model.getValueInRange({
            startLineNumber: position.lineNumber,
            startColumn: Math.max(1, position.column - 20),
            endLineNumber: position.lineNumber,
            endColumn: position.column,
          });

          // Suggest rule! unique matches
          if (textBeforeCursor.endsWith("rule!")) {
            return {
              suggestions: uniqueRuleMatchers.map((suggestion) => ({
                label: suggestion.name,
                kind: monacoInstance.languages.CompletionItemKind.Text,
                insertText: suggestion.name,
                range: {
                  startLineNumber: position.lineNumber,
                  startColumn: position.column - 5,
                  endLineNumber: position.lineNumber,
                  endColumn: position.column,
                },
              })),
            };
          }

          // Suggest pv! unique matches
          if (textBeforeCursor.endsWith("rv!")) {
            return {
              suggestions: uniqueRecordTypeMatchers.map((suggestion) => ({
                label: suggestion.name,
                kind: monacoInstance.languages.CompletionItemKind.Text,
                insertText: suggestion.name,
                range: {
                  startLineNumber: position.lineNumber,
                  startColumn: position.column - 3,
                  endLineNumber: position.lineNumber,
                  endColumn: position.column,
                },
              })),
            };
          }

          // Suggest dbService methods
          if (textBeforeCursor.endsWith("dbService.")) {
            const dbServiceMethods = [
              {
                label: "writeRecord",
                documentation: "Writes a new record into the database.",
                detail:
                  "writeRecord(String recordUuid, Map<String, Object> record)",
              },
              {
                label: "queryRecords",
                documentation:
                  "Queries records from the database based on a condition.",
                detail: "queryRecords(String recordUuid, String condition)",
              },
              {
                label: "updateRecord",
                documentation: "Updates records in the database.",
                detail:
                  "updateRecord(String recordUuid, Map<String, Object> updates, String condition)",
              },
              {
                label: "deleteRecord",
                documentation: "Deletes records from the database.",
                detail: "deleteRecord(String recordUuid, String condition)",
              },
            ];

            return {
              suggestions: dbServiceMethods.map((method) => ({
                label: method.label,
                kind: monacoInstance.languages.CompletionItemKind.Method,
                insertText: `${method.label}($0)`,
                insertTextRules:
                  monacoInstance.languages.CompletionItemInsertTextRule
                    .InsertAsSnippet,
                range: {
                  startLineNumber: position.lineNumber,
                  startColumn: position.column,
                  endLineNumber: position.lineNumber,
                  endColumn: position.column,
                },
                documentation: method.documentation,
                detail: method.detail,
              })),
            };
          }

          return { suggestions: [] };
        },
      }
    );

    return () => {
      provider.dispose();
    };
  };

  useEffect(() => {
    if (monaco) {
      const dispose = defineGroovyLanguage(monaco);
      return dispose;
    }
  }, [monaco, ruleMatchers]);

  // Editor options, including the custom theme and language
  const editorOptions = {
    selectOnLineNumbers: true,
    minimap: { enabled: false },
    readOnly: !isEditing,
  };

  const columns = [
    {
      Header: "Name",
      accessor: "name",
    },
    {
      Header: "Type",
      accessor: "type",
    },
    {
      Header: "Is Multiple",
      accessor: "isMultiple",
    },
    {
      Header: "Order",
      accessor: "order",
    },
    {
      Header: "",
      accessor: "actions",
    },
  ];

  const selectedRuleInputsData = selectedRuleInputs
    ? selectedRuleInputs.map((param, index) => ({
        name: (
          <Input
            value={param.paramNm}
            onChange={(e) => {
              const params = [...selectedRuleInputs];
              params[index] = {
                ...params[index],
                paramNm: e.target.value,
              };
              dispatch(setSelectedRuleInputData(params));
            }}
            width="10vw"
            disabled={!isEditing}
          />
        ),
        type: (
          <Box width="10vw">
            <VStack gap={3}>
              <DropDown
                selected={dataTypeOptions.find(
                  (type) => type.id === param.dataTypeCd
                )}
                title={<Text></Text>}
                options={dataTypeOptions}
                onOptionChange={function (
                  selectedOptions: Option | Option[]
                ): void {
                  if (!Array.isArray(selectedOptions)) {
                    const params = [...selectedRuleInputs];
                    params[index] = {
                      ...params[index],
                      dataTypeCd: selectedOptions.id,
                    };
                    dispatch(setSelectedRuleInputData(params));
                  }
                }}
                disabled={!isEditing}
              />
              {param.dataTypeCd === "ANY" && (
                <DropDown
                  selected={cdtOptions.find((cdt) => cdt.id === param.other)}
                  title={<Text></Text>}
                  options={cdtOptions}
                  onOptionChange={function (
                    selectedOptions: Option | Option[]
                  ): void {
                    if (!Array.isArray(selectedOptions)) {
                      const params = [...selectedRuleInputs];
                      params[index] = {
                        ...params[index],
                        other: selectedOptions.id,
                      };
                      dispatch(setSelectedRuleInputData(params));
                    }
                  }}
                  disabled={!isEditing}
                />
              )}
              {param.dataTypeCd === "RT" && (
                <DropDown
                  selected={recordTypeOptions.find(
                    (rt) => rt.id === param.other
                  )}
                  title={<Text></Text>}
                  options={recordTypeOptions}
                  onOptionChange={function (
                    selectedOptions: Option | Option[]
                  ): void {
                    if (!Array.isArray(selectedOptions)) {
                      const params = [...selectedRuleInputs];
                      params[index] = {
                        ...params[index],
                        other: selectedOptions.id,
                      };
                      dispatch(setSelectedRuleInputData(params));
                    }
                  }}
                  disabled={!isEditing}
                />
              )}
            </VStack>
          </Box>
        ),
        isMultiple: (
          <Center>
            <Checkbox
              defaultChecked
              variant="solid"
              colorScheme="brand"
              onChange={(e) => {
                const params = [...selectedRuleInputs];
                params[index] = {
                  ...params[index],
                  isMultiple: e.target.checked,
                };
                dispatch(setSelectedRuleInputData(params));
              }}
              isChecked={param.isMultiple}
              isReadOnly={!isEditing}
            />
          </Center>
        ),
        order: (
          <Center>
            <IconButton
              as={CiCircleChevUp as React.ElementType}
              boxSize="24px"
              aria-label="Move Up"
              onClick={() => {
                if (index > 0) {
                  const params = [...selectedRuleInputs];
                  [params[index], params[index - 1]] = [
                    params[index - 1],
                    params[index],
                  ]; // Swap with previous
                  dispatch(setSelectedRuleInputData(params));
                }
              }}
              variant="unstyled"
              isDisabled={!isEditing || index === 0}
            />
            <IconButton
              as={CiCircleChevDown as React.ElementType}
              boxSize="24px"
              aria-label="Move Down"
              onClick={() => {
                if (index < selectedRuleInputs.length - 1) {
                  const params = [...selectedRuleInputs];
                  [params[index], params[index + 1]] = [
                    params[index + 1],
                    params[index],
                  ]; // Swap with next
                  dispatch(setSelectedRuleInputData(params));
                }
              }}
              variant="unstyled"
              isDisabled={!isEditing || index === selectedRuleInputs.length - 1} // Disable for the last item
            />
          </Center>
        ),
        actions: isEditing ? (
          <Center>
            <Icon
              as={FaMinus as React.ElementType}
              color="red.500"
              cursor="pointer"
              onClick={() => {
                const params = [...allSelectedRuleInputs];
                const paramIndex = params.findIndex(
                  (param) => param.uuid === selectedRuleInputs[index].uuid
                );
                params[paramIndex] = {
                  ...selectedRuleInputs[index],
                  isActive: "N",
                };
                dispatch(setSelectedRuleInputData(params));
              }}
            />
          </Center>
        ) : null,
      }))
    : [];

  const columnData = [...selectedRuleInputsData];

  const handleTestInputChange = (index: number, value: string) => {
    const newTestInputs = [...selectedRuleInputs];
    newTestInputs[index] = {
      ...newTestInputs[index],
      testValue: value,
    };
    dispatch(setSelectedRuleInputData(newTestInputs));
  };

  const testInputColumns = [
    {
      Header: "Name",
      accessor: "name",
    },
    {
      Header: "Type",
      accessor: "type",
    },
    {
      Header: "Value",
      accessor: "value",
    },
  ];

  useEffect(() => {
    selectedRuleInputs.forEach((param, index) => {
      if (param.dataTypeCd === "BOOL") {
        const isValidBoolean =
          param.testValue === "true" || param.testValue === "false";
        if (!isValidBoolean) {
          handleTestInputChange(index, "true");
        }
      }

      if (param.dataTypeCd === "DATE") {
        const isValidDate = !isNaN(Date.parse(param.testValue || ""));
        if (!isValidDate) {
          const today = new Date().toISOString().split("T")[0];
          handleTestInputChange(index, today);
        }
      }

      // Handle date_time default values
      if (param.dataTypeCd === "DATE_TIME") {
        const isValidDateTime = !isNaN(Date.parse(param.testValue || ""));
        if (!isValidDateTime) {
          const now = new Date().toISOString();
          handleTestInputChange(index, now);
        }
      }
    });
  }, [selectedRuleInputs, handleTestInputChange]);

  const selectedRuleTestInputsData = selectedRuleInputs.map((param, index) => ({
    name: <Text>{param.paramNm}</Text>,
    type: <Text>{param.dataTypeCd}</Text>,
    value: ["ANY", "RT"].includes(param.dataTypeCd) ? (
      <Textarea
        value={param.testValue || ""}
        onChange={(e) => handleTestInputChange(index, e.target.value)}
      />
    ) : param.dataTypeCd === "BOOL" && !param.isMultiple ? (
      <RadioGroup
        defaultValue="true"
        onChange={(e) => handleTestInputChange(index, e)}
      >
        <Stack spacing={5} direction="row">
          <Radio colorScheme="brand" value="true">
            True
          </Radio>
          <Radio colorScheme="brand" value="false">
            False
          </Radio>
        </Stack>
      </RadioGroup>
    ) : param.dataTypeCd === "DATE" && !param.isMultiple ? (
      <Input
        value={param.testValue || ""}
        onChange={(e) => handleTestInputChange(index, e.target.value)}
        placeholder="Select Date and Time"
        size="md"
        type="date"
        colorScheme="brand"
      />
    ) : param.dataTypeCd === "DATE_TIME" && !param.isMultiple ? (
      <Input
        value={param.testValue || ""}
        onChange={(e) => handleTestInputChange(index, e.target.value)}
        placeholder="Select Date and Time"
        size="md"
        type="datetime-local"
        colorScheme="brand"
      />
    ) : (
      <Input
        value={param.testValue || ""}
        onChange={(e) => handleTestInputChange(index, e.target.value)}
      />
    ),
  }));

  const testInputColumnData = [...selectedRuleTestInputsData];

  const onSave = async () => {
    let hasError = false;

    selectedRuleInputs?.forEach((param) => {
      if (param.paramNm === "") {
        toast({
          title: "Error",
          description: "Param name cannot be empty.",
          status: "error",
          position: "top",
          duration: 3000,
          isClosable: true,
        });
        hasError = true;
      }
    });

    if (hasError) return;

    const data = {
      ruleUuid: selectedRule?.uuid,
      ruleBody: {
        id: selectedRule?.ruleBody ? selectedRule.ruleBody.id : 0,
        ruleBody: expressionHandler(selectedRuleBody),
      },
      ruleInputs: allSelectedRuleInputs.map((input) => ({
        id: input.id,
        uuid: input.uuid,
        paramNm: input.paramNm,
        paramIo: input.paramIo,
        paramOrder: input.paramOrder,
        dataTypCd: input.dataTypeCd,
        other: ["ANY", "RT"].includes(input.dataTypeCd) ? input.other : null,
        isMultiple: input.isMultiple,
        isActive: input.isActive,
      })),
    };

    const response = await getAxiosClient().post(
      "/api/eza_app_rule_io/update-rule-io",
      data
    );

    dispatch(fetchRulesById(ruleId as string));

    if (isEditing) onEditToggle();
  };

  const areArraysEqual = (arr1: RuleInput[], arr2: RuleInput[]): boolean => {
    const array1 = arr1.filter((i) => i.isActive === "Y");
    const array2 = arr2.filter((i) => i.isActive === "Y");
    if (array1.length !== array2.length) return false;

    const ruleInputsEdited = array1.every((item1, index) => {
      const item2 = array2[index];
      return (
        item1.uuid === item2.uuid &&
        item1.paramNm === item2.paramNm &&
        item1.paramOrder === item2.paramOrder &&
        item1.dataTypeCd === item2.dataTypeCd &&
        item1.paramOrder === item2.paramOrder &&
        item1.isActive === item2.isActive
      );
    });

    const ruleBodyEdited =
      selectedRule?.ruleBody?.ruleBody === selectedRuleBody;

    return ruleInputsEdited && ruleBodyEdited;
  };

  const isEdited =
    selectedRule?.ruleInputs && selectedRuleInputs
      ? !areArraysEqual(selectedRule.ruleInputs, selectedRuleInputs)
      : false;

  const onAddField = () => {
    const params: RuleInput[] = [
      {
        id: 0,
        uuid: "",
        ruleUuid: "",
        paramNm: "",
        paramOrder: "",
        dataTypeCd: "STR",
        other: cdtOptions.length > 0 ? cdtOptions[0].id : undefined,
        isMultiple: false,
        paramIo: "I",
        isActive: "Y",
        createdBy: "",
        createdOn: "",
        updatedBy: "",
        updatedOn: "",
      },
      ...selectedRuleInputs,
    ];
    dispatch(setSelectedRuleInputData(params));
  };

  const setInputBody = (body: string) => {
    dispatch(setSelectedRuleBody(body));
  };

  const executeRule = async (
    ruleBody: string,
    params: { [key: string]: any }
  ) => {
    const data = {
      uuid: ruleId,
      inputParams: params,
    };

    try {
      const response = await fetch(
        (process.env.REACT_APP_RULE_ENGINE_URL as string) + "/execute-new",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        }
      );

      // Check response status
      if (response.ok) {
        const responseData = await response.text();

        try {
          const result = JSON.parse(responseData);
          setResponseData(result);
        } catch (error) {
          setResponseData(responseData);
        }

        setStatus("success");
      } else {
        setStatus("failed");
        setResponseData(await response.text());
      }
    } catch (error) {
      setStatus("failed");
      if (error instanceof Error) {
        setResponseData(error.message);
      } else {
        setResponseData("An unknown error occurred.");
      }
    }
  };

  const checkForNestedRules = async (result: { [key: string]: any }) => {
    const regex = /rule!([a-f0-9\-]{36})\(([^)]*)\)/g;

    if (selectedRuleBody) {
      let processedBody = selectedRuleBody;

      // Find all 'rule!' expressions in the rule body
      while (true) {
        const matchResult = regex.exec(processedBody);
        if (matchResult) {
          const [fullMatch, ruleId, variableString] = matchResult;

          const rule = rules.find((rule) => rule.uuid === ruleId);

          if (rule) {
            const params: { [key: string]: any } = {};
            const variableParams = variableString
              .split(", ")
              .map((param) => result[param]);

            rule.ruleInputs.forEach(
              (param, index) => (params[param.paramNm] = variableParams[index])
            );

            executeRule(rule.ruleBody?.ruleBody as string, params);

            processedBody = processedBody.replace(fullMatch, responseData);
            console.log(processedBody, fullMatch, responseData);
          }
        } else {
          executeRule(processedBody, result);
          break;
        }
      }
    }
  };

  const onTextInput = () => {
    let hasError = false;
    const result: { [key: string]: any } = {};

    // Iterate over the testInputs and process values based on dataTypeCd
    selectedRuleInputs.forEach((param) => {
      const { paramNm, testValue, dataTypeCd, isMultiple } = param;
      var array = [];

      // If parameter name is missing, show error
      if (!paramNm) {
        toast({
          title: "Error",
          description: "Param name cannot be empty.",
          status: "error",
          position: "top",
          duration: 3000,
          isClosable: true,
        });
        hasError = true;
        return;
      }
      try {
        if (isMultiple && Array.isArray(JSON.parse(testValue as string))) {
          array = JSON.parse(testValue as string);
        } else if (isMultiple) throw new Error("Not valid array");
      } catch (error) {
        console.log("error", error);
        toast({
          title: "Error",
          description: `Input for parameter "${paramNm}" must be a valid Array.`,
          status: "error",
          position: "top",
          duration: 3000,
          isClosable: true,
        });
        hasError = true;
      }

      if (["ANY", "RT"].includes(dataTypeCd)) {
        // For 'ANY' type, validate and parse JSON
        try {
          result[paramNm] = JSON.parse(testValue || "");

          if (!isMultiple) {
            const keys = Object.keys(result[paramNm]);
            const dt = cdt.find((option) => option.uuid === param.other);
            const rt = recordTypes.find(
              (option) => option.uuid === param.other
            );

            if (dt) {
              dt.cdtAttributes.forEach((attr) => {
                if (attr.isMandatory && !keys.includes(attr.name)) {
                  toast({
                    title: "Error",
                    description: `Attribute ${attr.name} is not present for ${paramNm}`,
                    status: "error",
                    position: "top",
                    duration: 3000,
                    isClosable: true,
                  });
                  hasError = true;
                }
              });
            } else if (rt) {
              console.log(rt);
            }
          } else {
            result[paramNm].forEach((obj: any) => {
              const keys = Object.keys(obj);
              const dt = cdt.find((option) => option.uuid === param.other);

              if (dt) {
                dt.cdtAttributes.forEach((attr) => {
                  if (attr.isMandatory && !keys.includes(attr.name)) {
                    toast({
                      title: "Error",
                      description: `Attribute ${attr.name} is not present for ${paramNm}`,
                      status: "error",
                      position: "top",
                      duration: 3000,
                      isClosable: true,
                    });
                    hasError = true;
                  }
                });
              } else {
                toast({
                  title: "Error",
                  description: `Selected a custom data type for ${paramNm}`,
                  status: "error",
                  position: "top",
                  duration: 3000,
                  isClosable: true,
                });
                hasError = true;
              }
            });
          }
        } catch (e) {
          toast({
            title: "Error",
            description: `Input for parameter "${paramNm}" must be a valid JSON.`,
            status: "error",
            position: "top",
            duration: 3000,
            isClosable: true,
          });
          hasError = true;
        }
      } else if (dataTypeCd === "INT") {
        // For 'INT' type, validate and convert to integer
        if (isMultiple) {
          const castedArray = array.map((it: any) => parseInt(it));
          if (
            castedArray !== 0 &&
            castedArray.filter((it: any) => isNaN(it)).length !== 0
          ) {
            toast({
              title: "Error",
              description: `Input for parameter "${paramNm}" must be a valid integer array.`,
              status: "error",
              position: "top",
              duration: 3000,
              isClosable: true,
            });
            hasError = true;
          } else {
            result[paramNm] = castedArray;
          }
        } else {
          const intValue = parseInt(testValue || "", 10);
          if (isNaN(intValue)) {
            toast({
              title: "Error",
              description: `Input for parameter "${paramNm}" must be a valid integer.`,
              status: "error",
              position: "top",
              duration: 3000,
              isClosable: true,
            });
            hasError = true;
          } else {
            result[paramNm] = intValue;
          }
        }
      } else if (dataTypeCd === "NUM_DOUB") {
        if (isMultiple) {
          const castedArray = array.map((it: any) => parseFloat(it));
          if (
            castedArray !== 0 &&
            castedArray.filter((it: any) => isNaN(it)).length !== 0
          ) {
            toast({
              title: "Error",
              description: `Input for parameter "${paramNm}" must be a valid double array.`,
              status: "error",
              position: "top",
              duration: 3000,
              isClosable: true,
            });
            hasError = true;
          } else {
            result[paramNm] = castedArray;
          }
        } else {
          const doubleValue = parseFloat(testValue || "");
          if (isNaN(doubleValue)) {
            toast({
              title: "Error",
              description: `Input for parameter "${paramNm}" must be a valid double.`,
              status: "error",
              position: "top",
              duration: 3000,
              isClosable: true,
            });
            hasError = true;
          } else {
            result[paramNm] = doubleValue;
          }
        }
      } else if (dataTypeCd === "BOOL") {
        if (isMultiple) {
          const castedArray = array.map((it: any) => it === "true");
          if (
            castedArray !== 0 &&
            castedArray.filter((it: any) => isStringLiteral(it)).length !== 0
          ) {
            toast({
              title: "Error",
              description: `Input for parameter "${paramNm}" must be a valid boolean array.`,
              status: "error",
              position: "top",
              duration: 3000,
              isClosable: true,
            });
            hasError = true;
          } else {
            result[paramNm] = castedArray;
          }
        } else {
          const booleanValue = testValue === "true";
          if (booleanValue === null) {
            toast({
              title: "Error",
              description: `Input for parameter "${paramNm}" must be a valid boolean.`,
              status: "error",
              position: "top",
              duration: 3000,
              isClosable: true,
            });
            hasError = true;
          } else {
            result[paramNm] = booleanValue;
          }
        }
      } else if (dataTypeCd === "STR") {
        if (isMultiple) {
          const castedArray = array.map((it: any) => it.toString());
          if (
            castedArray !== 0 &&
            castedArray.filter((it: any) => isStringLiteral(it)).length !== 0
          ) {
            toast({
              title: "Error",
              description: `Input for parameter "${paramNm}" must be a valid string array.`,
              status: "error",
              position: "top",
              duration: 3000,
              isClosable: true,
            });
            hasError = true;
          } else {
            result[paramNm] = castedArray;
          }
        } else {
          // For 'STR' type, validate and convert to integer
          if (!testValue) {
            toast({
              title: "Error",
              description: `Input for parameter "${paramNm}" must be a valid string.`,
              status: "error",
              position: "top",
              duration: 3000,
              isClosable: true,
            });
            hasError = true;
          } else {
            result[paramNm] = testValue;
          }
        }
      } else {
        // For other types, treat the value as a string
        result[paramNm] = testValue;
      }
    });

    // If there's no error, proceed with further logic
    console.log(result, hasError);
    if (!hasError) {
      executeRule("", result);
    }
  };

  const expressionHandler = (expression: string | undefined) => {
    const matchers = [...recordTypeMatchers, ...ruleMatchers];

    matchers.sort((a, b) => b.name.length - a.name.length);

    matchers.forEach((match) => {
      const regex = new RegExp(match.name, "g");
      expression = expression?.replace(regex, `"${match.value}"`);
    });

    return expression;
  };

  function expressionHandlerReverse(expression: string | undefined) {
    const uuidRegex =
      /"\b[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}\b"/;

    const matches = expression?.match(uuidRegex);
    const matchers = [...recordTypeMatchers, ...ruleMatchers];

    matches?.forEach((match) => {
      const obj = matchers.find((ob) => `"${ob.value}"` === match);

      if (obj) {
        const regex = new RegExp(match, "g");
        expression = expression?.replace(regex, obj.name);
      }
    });

    return expression;
  }

  return {
    editorOptions,
    selectedRule,
    selectedRuleBody,
    columns,
    columnData,
    testInputColumns,
    testInputColumnData,
    isEditing,
    isEdited,
    responseData,
    status,
    ruleMatchers,
    onEditToggle,
    onSave,
    onAddField,
    setInputBody,
    onTextInput,
  };
};
